import { ItiLink as Link } from 'components';
import DataTable, { DataTableColumn } from 'components/tables/data-table';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPaginated } from 'services';
import { downloadCsv } from 'services/download-csv';
import { Role } from 'types';

const LocationsTab: FC = () => {
  const navigate = useNavigate();

  const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(true);

  const columns: DataTableColumn[] = useMemo(
    () => [
      {
        field: 'id',
        label: 'ID',
        width: 80,
        renderCell: value => <Link to={`/settings/locations/${value}`}>{value}</Link>
      },
      {
        field: 'name',
        label: 'NAME',
        width: 80,
        renderCell: (value, row) => <Link to={`/settings/locations/${row.id}`}>{value}</Link>
      },
      { field: 'code', label: 'CODE', width: 80 },
      { field: 'remitTo', label: 'REMIT TO', width: 80 },
      { field: 'address', label: 'ADDRESS', width: 80 },
      { field: 'address2', label: 'ADDRESS 2', width: 80 },
      { field: 'zipCode', label: 'ZIP CODE', width: 80 },
      { field: 'country', label: 'COUNTRY', width: 80 },
      { field: 'city', label: 'CITY', width: 80 },
      { field: 'state', label: 'STATE', width: 60 },
      { field: 'timeZone', label: 'TIME ZONE', width: 120 },
      { field: 'utcOffset', label: 'UTC OFFSET', width: 60, type: 'number' },
      { field: 'createdBy', label: 'CREATED BY', width: 80, allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN] },
      {
        field: 'createdDate',
        label: 'CREATED DATE',
        width: 120,
        type: 'dateTime',
        dateFormat: 'M/D/YY LT'
      },
      { field: 'lastUpdatedBy', label: 'LAST UPDATED BY', width: 80, allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN] },
      {
        field: 'lastUpdatedDate',
        label: 'LAST UPDATED DATE',
        width: 120,
        type: 'dateTime',
        dateFormat: 'M/D/YY LT'
      },
      {
        field: 'deleted',
        label: 'STATUS',
        width: 60,
        renderCell: value => <p>{value ? 'Deactivated' : 'Active'}</p>,
        skipQuickSearch: true,
        type: 'boolean'
      }
    ],
    []
  );

  const initialColumns = useMemo(() => {
    const init = new Set(['name', 'code', 'city', 'state', 'timeZone', 'createdDate', 'deleted']);
    return columns.filter(({ field }) => init.has(field)).map(({ field, width }) => ({ field, width }));
  }, [columns]);

  return (
    <div id="locations-table">
      <div className="flex-fill d-flex justify-content-end g-3">
        <button
          id="create-location"
          className="btn-primary"
          onClick={() => navigate('/settings/locations/new')}
          data-cy="new-location-button"
        >
          NEW LOCATION
        </button>
      </div>
      <DataTable
        fetchCsv={downloadCsv('locations')}
        fetchData={fetchPaginated('locations/page', { includeDeactivated })}
        initialColumns={initialColumns}
        columns={columns}
        searchAdornment={
          <div className="d-flex align-items-center g-3">
            <label className="text-info-dark">SHOW DEACTIVATED</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={includeDeactivated === true}
                onChange={e => setIncludeDeactivated(e.target.checked)}
              />
              <span className="slider round" />
            </label>
          </div>
        }
      />
    </div>
  );
};

export default LocationsTab;
